import React from "react"
import { useNavigate } from "react-router-dom"
import { X, Clock, Users, MapPin, Video, Sparkles, Mic, ExternalLink, Play, User } from "lucide-react"
import type { CalendarEvent } from "../models/calendar.model"
import { getEventPalette } from "../utils/colorPalette"
import { Button } from "@/clientWeb/components/ui/button"
import { useLanguage } from "@/clientWeb/i18n/LanguageContext"
import { formatOccurrenceDay } from "@/features/meetings/utils/recurrence"
import { useAuthStore } from "@/features/auth/store/auth.store"

const TYPE_ICON: Record<CalendarEvent["type"], React.ReactNode> = {
  meeting: <Video className="w-4 h-4" />,
  webinar: <Sparkles className="w-4 h-4" />,
  call: <Mic className="w-4 h-4" />,
}

const STATUS_COLORS: Record<CalendarEvent["status"], string> = {
  upcoming: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300",
  ongoing: "bg-red-100 text-red-600 dark:bg-red-900/40 dark:text-red-300",
  completed: "bg-muted text-muted-foreground",
}

interface Props {
  event: CalendarEvent
  onClose: () => void
}

export default function EventPopover({ event, onClose }: Props) {
  const { t, language } = useLanguage()
  const navigate = useNavigate()
  const userName = useAuthStore((s) => s.user?.name) ?? "Guest"
  const palette = getEventPalette(event)
  const softAccent = palette.light

  const STATUS_LABELS: Record<CalendarEvent["status"], string> = {
    upcoming: t('calendar.upcoming'),
    ongoing: t('calendar.live'),
    completed: t('calendar.completed'),
  }
  const dateLabel = event.date.toLocaleDateString("en-US", {
    weekday: "long", month: "long", day: "numeric", year: "numeric",
  })

  /** Route table — meeting vs webinar entities navigate to their own
   *  detail / join surfaces. Falls back to a no-op for legacy mock rows
   *  (`entityKind` unset). */
  const goToDetails = () => {
    if (event.entityKind === "meeting" && event.entityId) {
      navigate(`/meetings/${event.entityId}`)
    } else if (event.entityKind === "webinar" && event.entityId) {
      navigate(`/webinar/${event.entityId}`)
    }
    onClose()
  }

  const goToJoin = () => {
    if (event.entityKind === "meeting" && event.entityId) {
      navigate(`/meeting/${event.joinId ?? event.entityId}?name=${encodeURIComponent(userName)}`)
    } else if (event.entityKind === "webinar" && event.entityId) {
      navigate(`/webinar/${event.entityId}`)
    }
    onClose()
  }

  const hasEntity = !!event.entityId
  // A later day of a recurring series: the server would refuse the join.
  const lockedUntil = event.joinLockedUntilMs ?? null

  return (
    <div
      className="bg-card rounded-2xl shadow-2xl border border-border w-72 overflow-hidden"
      onClick={e => e.stopPropagation()}
    >
      {/* Color header bar */}
      <div className="h-2 w-full" style={{ background: softAccent }} />

      <div className="p-4">
        {/* Header row */}
        <div className="flex items-start justify-between gap-2 mb-3">
          <div className="flex items-start gap-2 min-w-0 flex-1">
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
              style={{ background: softAccent, color: palette.text }}
            >
              {TYPE_ICON[event.type]}
            </div>
            <div className="min-w-0 flex-1">
              <h3
                className="text-sm font-semibold text-foreground leading-tight line-clamp-2 break-words"
                title={event.title}
              >
                {event.title}
              </h3>
              <span className={`inline-block mt-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${STATUS_COLORS[event.status]}`}>
                {STATUS_LABELS[event.status]}
              </span>
            </div>
          </div>
          <button
            onClick={onClose}
            className="shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
          >
            <X size={14} />
          </button>
        </div>

        {/* Description — line-clamped to 3 lines; full text on hover/title attr */}
        {event.description && (
          <p
            className="text-xs text-muted-foreground mb-3 leading-relaxed line-clamp-3 break-words"
            title={event.description}
          >
            {event.description}
          </p>
        )}

        {/* Details */}
        <div className="space-y-2 mb-4">
          <div className="flex items-center gap-2 text-xs text-foreground/80">
            <Clock size={13} className="text-muted-foreground shrink-0" />
            <span>{dateLabel}</span>
          </div>
          <div className="flex items-center gap-2 text-xs text-foreground/80">
            <Clock size={13} className="text-muted-foreground shrink-0" />
            <span>{event.startTime} – {event.endTime}</span>
          </div>
          {event.hostName && (
            <div className="flex items-center gap-2 text-xs text-foreground/80">
              <User size={13} className="text-muted-foreground shrink-0" />
              <span className="truncate">{event.hostName}</span>
            </div>
          )}
          {event.participants && (
            <div className="flex items-center gap-2 text-xs text-foreground/80">
              <Users size={13} className="text-muted-foreground shrink-0" />
              <span>{event.participants} {event.participants > 1 ? t('calendar.participants') : t('calendar.participant')}</span>
            </div>
          )}
          {event.location && (
            <div className="flex items-center gap-2 text-xs text-foreground/80">
              <MapPin size={13} className="text-muted-foreground shrink-0" />
              <span>{event.location}</span>
            </div>
          )}
        </div>

        {/* Action buttons */}
        <div className="flex flex-col gap-2">
          {event.status === "upcoming" && (
            <Button
              onClick={goToJoin}
              disabled={!hasEntity || lockedUntil !== null}
              className="w-full bg-emerald-500 hover:bg-emerald-600 text-white"
            >
              <Play size={13} />
              {t('calendar.startMeeting')}
            </Button>
          )}
          {event.status === "upcoming" && lockedUntil !== null && (
            <p className="text-xs text-center text-muted-foreground">
              {t('meetings.series.joinOpensOn', { date: formatOccurrenceDay(lockedUntil, language) })}
            </p>
          )}
          {event.status === "ongoing" && (
            <Button onClick={goToJoin} disabled={!hasEntity} className="w-full">
              <ExternalLink size={13} />
              {t('calendar.joinMeeting')}
            </Button>
          )}
          {hasEntity && (
            <Button variant="outline" onClick={goToDetails} className="w-full">
              {t('common.viewDetails')}
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}
